import { Hub } from "../hub"
import { IHub } from "../interfaces/IHub"
import {
    ApiConfirmation,
    ApiMessage,
    AutoHoldingMessage,
    BadgeHoldStatusUpdate,
    HubPinnedMessages,
    isApiMessage,
    isConfirmationMessage,
    isDeletedByClientMessage,
    isUserTypingMessage,
    MessageStatus,
    MessageTextType,
    MessageType,
    SignalRConfirmationType,
    UserTypingType
} from "../../models/Dialogs/message"
import {
    DialogBadge,
    GetDialogAttachment,
    DialogFilesPageResponse,
    DialogFilesPaginatedRequest
} from "../../models/Dialogs/dialog"
import { Store } from "../../store/store"
import i18next from "i18next"
import { actions } from "../../store/dialogs/slice"
import {
    handleEvents,
    isCurrentUserSender,
    isScreensharingMessage,
    isStarterMessage,
    isSystemMessage,
    notifyOperator
} from "./helpers"
import { logHubError } from "../../utility/common/logError"
import { NotificationsTypes } from "../../utility/common/TabNotifications"
import { dialogsApi, updateDialogBadges } from "../controllers/dialogs"
import {
    refetchActiveDialogsCount,
    refetchDialogBadges,
    selectDialogBadges,
    updateCurrentDialogBadgeWithOutgoing,
    updateDialogBadge,
    updateOperatorClientsBadges,
    upsertDialogBadges
} from "../utility/dialogs.helpers"
import { DialogsHubEvents, DialogsHubEventTypes as Events } from "../../models/Dialogs/hub"
import { selectDialogId, selectMessagesPaginationState } from "../../store/dialogs/selectors"
import { getAIAssistHint, getAISuggestsByDialogId } from "../../store/dialogs/thunks"
import { getClientFullname } from "../../utility/dialogs/client"
import { MESSAGES_PER_PAGE } from "../../utility/dialogs/constant"
import { Dispatch } from "../../utility/common/storeHelper"
import { selectCurrentProjectId } from "../../store/projects/selectors"
import { updateLinkedClients } from "../../components/LinkClientToOperatorPopover/utils/updateLinkedClients"
import { getLinkedClientFromSubject } from "../../components/LinkClientToOperatorPopover/utils/getLinkedClientFromSubject"
import { isShowLinkedClients } from "../../components/LinkClientToOperatorPopover/utils/isShowLinkedClients"
import { UserAccessActions } from "../../models/knowledgeBase/userAccess"
import { OLD_WORKPLACE_FRAME_NAME } from "../../components/OldWorkplaceFrame/OldWorkplaceFrame"
import { getFileFirstLastNames } from "./helpers"
import { selectDraftById } from "../../store/drafts/selectors"
import { setMessageInputDraft } from "../../store/drafts/actions"
import {selectCurrentUserLanguage} from "../../store/users/selectors";

const tDialogsNotificationsNamespace = "dialogs:notifications."

const HUB_NAME = "DialogsHub"

export class DialogsHub {
    private _hub: IHub
    private _store: Store
    private _events: DialogsHubEvents

    constructor(store: Store) {
        const reduxState = store.getState()
        const useAllTransportSignalR =
            reduxState.config.config.data?.WebConfig.appSettings.useAllTransportSignalR === true

        this._hub = new Hub(`/dialogs-hub-v2`, useAllTransportSignalR)
        this._store = store

        this._events = this.createEvents(this._store, this._store.dispatch)
        this._hub.reconnectedCallback = async () => {
            await this.subscribe()
            this.registerServerEvents()
        }
    }

    async subscribe() {
        await this._hub.subscribe("Subscribe")
    }

    async unsubscribe() {
        await this._hub.unsubscribe("Unsubscribe")
    }

    getInstance(): IHub {
        return this._hub
    }

    private checkOldWorkplaceLoaded() {
        const oldWPNode = document.querySelector<HTMLIFrameElement>(`[name="${OLD_WORKPLACE_FRAME_NAME}"]`)

        // Frame's display attribute is inline-block by default
        if (oldWPNode && getComputedStyle(oldWPNode).display === "inline") {
            const oldDialogsNode = oldWPNode.contentDocument?.querySelector("#dialogs")

            if (oldDialogsNode && getComputedStyle(oldDialogsNode).display === "block") {
                throw new Error("Old workspace frame node already loaded. Passing")
            }
        }
    }

    // TODO: https://gitlab.crafttalk.ru/aibotdev/core/opbot/-/merge_requests/6872#note_47279
    private createEvents(store: Store, dispatch: Dispatch) {
        return {
            [Events.DETACH_DIALOG]: async (data: unknown) => {
                try {
                    const { Id: detachedDialogId } = data as { Id: string }
                    const selectedDialogId = selectDialogId(this._store.getState())
                    const dialogBadges = selectDialogBadges()
                    const projectId = selectCurrentProjectId(store.getState())
                    const isSelectedDialogDetached = selectedDialogId === detachedDialogId
                    let nextCurrentDialogId = ""
                    let nextCurrentClientId = ""

                    if (dialogBadges && dialogBadges.length > 0) {
                        const client = dialogBadges.find(
                            dialogBadge => dialogBadge.Id === detachedDialogId && projectId === dialogBadge.Project.Id
                        )?.Client
                        const filteredDialogBadges = dialogBadges.filter(
                            dialogBadge => dialogBadge.Id !== detachedDialogId
                        )

                        if (filteredDialogBadges.length > 0) {
                            nextCurrentDialogId = filteredDialogBadges[0].Id
                            nextCurrentClientId = filteredDialogBadges[0].Client.OmniUserId
                        }

                        if (client) {
                            const operatorClientsBadgesSelector = dialogsApi.endpoints.getOperatorClientsBadges.select()
                            const operatorClientsBadges = operatorClientsBadgesSelector(store.getState())

                            if (Array.isArray(operatorClientsBadges.data) && projectId) {
                                dispatch(updateOperatorClientsBadges(client, projectId))
                            }
                        }

                        if (isSelectedDialogDetached) {
                            dispatch(
                                actions.setMessagesPaginationState({
                                    OmniUserId: nextCurrentClientId,
                                    ProjectId: projectId,
                                    FromTodayDialogs: true,
                                    StartTime: 0,
                                    Count: MESSAGES_PER_PAGE
                                })
                            )
                        }
                        dispatch(upsertDialogBadges(filteredDialogBadges))
                    }

                    dispatch(refetchActiveDialogsCount())

                    if (isSelectedDialogDetached) {
                        dispatch(actions.setCurrentDialogId(nextCurrentDialogId))
                        dispatch(actions.setCurrentClientOmniUserId(nextCurrentClientId))
                        const draft = selectDraftById(store.getState(), nextCurrentClientId ?? "")
                        const storedMessage = draft?.content ?? ""
                        dispatch(setMessageInputDraft(storedMessage))
                    }

                    dispatch(
                        actions.setIsUserTyping({
                            isTyping: false,
                            message: ""
                        })
                    )
                } catch (e) {
                    logHubError(HUB_NAME, Events.DETACH_DIALOG, e)
                }
            },
            [Events.HANDLE_ROUTED_DIALOG]: async (data: unknown) => {
                try {
                    this.checkOldWorkplaceLoaded()

                    const { Badge: badge } = data as { Badge: DialogBadge }
                    const dialogId = badge.Id
                    const projectId = badge.Project.Id
                    const dialogBadges = selectDialogBadges()
                    const title = i18next.t(`${tDialogsNotificationsNamespace}new-dialog`)
                    const clientFullname =
                        getClientFullname(badge.Client) || i18next.t(`${tDialogsNotificationsNamespace}unknown-client`)
                    const body = i18next.t(`${tDialogsNotificationsNamespace}client`) + " " + clientFullname
                    const selectedDialogId = selectDialogId(store.getState())
                    const clientOmniUserId = badge.Client.OmniUserId

                    await dispatch(
                        upsertDialogBadges([
                            badge,
                            ...(dialogBadges?.filter(existingBadge => existingBadge.Id !== badge.Id) || [])
                        ])
                    )

                    if (!selectedDialogId) {
                        dispatch(actions.setCurrentDialogId(dialogId))
                        dispatch(actions.setCurrentClientOmniUserId(clientOmniUserId))
                    }

                    if (dialogBadges && !selectedDialogId) {
                        dispatch(
                            dialogsApi.endpoints.getDialogTopics.initiate({
                                ProjectId: projectId
                            })
                        )
                        dispatch(
                            actions.setMessagesPaginationState({
                                OmniUserId: clientOmniUserId,
                                ProjectId: projectId,
                                StartTime: 0,
                                Count: MESSAGES_PER_PAGE
                            })
                        )
                    }

                    dispatch(refetchActiveDialogsCount())

                    notifyOperator(
                        {
                            title,
                            body,
                            projectId,
                            dialogId,
                            clientId: badge.Client.OmniUserId,
                            dispatch: store.dispatch
                        },
                        { type: NotificationsTypes.DialogRouted }
                    )
                } catch (e) {
                    logHubError(HUB_NAME, Events.HANDLE_ROUTED_DIALOG, e)
                }
            },
            // TODO: Зарефакторить
            [Events.HANDLE_MESSAGE]: async (message: unknown) => {
                try {
                    this.checkOldWorkplaceLoaded()

                    const currentMessagePagination = selectMessagesPaginationState(store.getState())
                    const selectedDialogId = selectDialogId(store.getState())
                    const projectId = selectCurrentProjectId(store.getState()) ?? ""
                    const selectedDialogInstance = dialogsApi.endpoints.getDialog.select(selectedDialogId ?? "")(
                        store.getState()
                    )
                    const dialogProjectId = selectedDialogInstance.data?.Project.Id ?? projectId

                    if (isScreensharingMessage(message)) {
                        const currentMessagePagination = selectMessagesPaginationState(store.getState())

                        if (currentMessagePagination) {
                            dispatch(
                                dialogsApi.util.updateQueryData(
                                    "getDialogMessages",
                                    currentMessagePagination,
                                    oldState => {
                                        oldState.Messages.push({
                                            ...message,
                                            Fields: { ...message.Fields, Sender: { Id: "" } }
                                        })
                                    }
                                )
                            )
                        }
                        return
                    }

                    if (isUserTypingMessage(message)) {
                        const { Type, DialogId, Text } = message.Fields

                        if (selectedDialogId !== DialogId) {
                            return
                        }

                        const isUserTyping = Type === UserTypingType.Typing

                        dispatch(
                            actions.setIsUserTyping({
                                isTyping: isUserTyping,
                                message: isUserTyping ? Text : ""
                            })
                        )
                    }

                    if (isDeletedByClientMessage(message)) {
                        if (selectedDialogId === message.Fields.DialogId && currentMessagePagination) {
                            let isUpdateDialogBage = false

                            dispatch(
                                dialogsApi.util.updateQueryData(
                                    "getDialogMessages",
                                    currentMessagePagination,
                                    oldState => {
                                        const deletedMessageIndex = oldState.Messages.findIndex(
                                            msg => msg.Fields.Id === message.Fields.Id
                                        )
                                        if (deletedMessageIndex !== -1) {
                                            oldState.Messages[deletedMessageIndex] = message

                                            if (deletedMessageIndex === 0) {
                                                isUpdateDialogBage = true
                                            }
                                        }
                                    }
                                )
                            )

                            if (isUpdateDialogBage) {
                                dispatch(
                                    updateDialogBadges(oldState => {
                                        const idxIntoList = oldState.findIndex(
                                            x => x.Client.OmniUserId === message.Fields.Sender.Id
                                        )

                                        if (idxIntoList > -1) {
                                            oldState[idxIntoList].Preview =
                                                "dialogs:dialogs-list.description.deleted-by-client"
                                        }
                                    })
                                )
                            }
                        }
                    }

                    if (isConfirmationMessage(message)) {
                        if (selectedDialogId === message.Fields.DialogId && currentMessagePagination) {
                            dispatch(
                                dialogsApi.util.updateQueryData(
                                    "getDialogMessages",
                                    currentMessagePagination,
                                    oldState => {
                                        const messageContent = (message as unknown as ApiConfirmation).Fields
                                        const messageIndex = oldState.Messages.findIndex(
                                            msg => msg.Fields.Id === messageContent.RelatedMessage
                                        )

                                        if (messageIndex !== -1) {
                                            switch (messageContent.Type) {
                                                case SignalRConfirmationType.Sent:
                                                    ;(oldState.Messages[messageIndex] as ApiMessage).Fields.Status =
                                                        MessageStatus.Sent
                                                    break
                                                case SignalRConfirmationType.Read:
                                                    ;(oldState.Messages[messageIndex] as ApiMessage).Fields.Status =
                                                        MessageStatus.Read
                                                    break
                                                case SignalRConfirmationType.Failed:
                                                    ;(oldState.Messages[messageIndex] as ApiMessage).Fields.Status =
                                                        MessageStatus.Failed
                                                    break
                                            }
                                        }
                                    }
                                )
                            )
                        }

                        return
                    }

                    if (!isApiMessage(message)) {
                        return
                    }

                    if (isSystemMessage(message)) {
                        return
                    }

                    if (isStarterMessage(message)) {
                        // https://youtrack.craft-talk.ru/issue/CLOUD-4670#focus=Comments-4-71343.0-0
                        message.Fields.Direction = 1
                        const currentOperator = store.getState().users.currentUser
                        message.Fields.Sender.Id =
                            currentOperator.data?.Login || `DEFAULT___${currentOperator.data?.Login}`
                    }

                    let dialogBadges = selectDialogBadges()
                    const isCached = dialogBadges?.some(x => x.Client.OmniUserId === message.Fields.Sender.Id)

                    if (message.Fields.Attachments?.length) {
                        const projectSettings = store.getState().project.settings
                        const enableFileSectionCollectFromWholeDialog =
                            projectSettings?.Operator?.NewWorkplace?.EnableFileSectionCollectFromWholeDialog === true

                        if (enableFileSectionCollectFromWholeDialog) {
                            const queryCache = store.getState().queryApi?.queries || {}
                            Object.entries(queryCache).forEach(([queryCacheKey, queryData]) => {
                                if (queryCacheKey.startsWith("getDialogFilesPage")) {
                                    const originalArgs = queryData?.originalArgs as DialogFilesPaginatedRequest
                                    if (originalArgs.FileSections.length === 0) {
                                        const newSectionedAttachments: DialogFilesPageResponse[] =
                                            message.Fields.Attachments.map(({ Url, Name, Size }) => {
                                                const { UploaderFirstName, UploaderLastName } = getFileFirstLastNames({
                                                    message,
                                                    store,
                                                    dialogBadges
                                                })

                                                return {
                                                    Url: Url.slice(Url.indexOf("/file")),
                                                    Title: Name,
                                                    File_size: Size,
                                                    File_sections: [],
                                                    Meta: {
                                                        uploader_firstname: UploaderFirstName,
                                                        omni_user_id: originalArgs.OmniUserId,
                                                        dialog_id: originalArgs.OmniUserId,
                                                        uploader_lastname: UploaderLastName,
                                                        is_reply: Boolean(originalArgs.IsReply) ? "True" : "False"
                                                    },
                                                    Uploaded_at: Date.now()
                                                }
                                            })

                                        dispatch(
                                            dialogsApi.util.updateQueryData(
                                                "getDialogFilesPage",
                                                originalArgs,
                                                draft => {
                                                    draft.unshift(...newSectionedAttachments)
                                                }
                                            )
                                        )
                                    }
                                }
                            })
                        } else {
                            const newDefaultAttachments: GetDialogAttachment[] = message.Fields.Attachments.map(
                                ({ Url, Name, Size }) => {
                                    const { UploaderFirstName, UploaderLastName } = getFileFirstLastNames({
                                        message,
                                        store,
                                        dialogBadges
                                    })

                                    return {
                                        Url: Url.slice(Url.indexOf("/file")),
                                        Name,
                                        Size: Size,
                                        UploadedAt: message.Fields.AddedDate,
                                        IsOperator: isCurrentUserSender(message, store),
                                        UploaderFirstName,
                                        UploaderLastName
                                    }
                                }
                            )

                            dispatch(
                                dialogsApi.util.updateQueryData(
                                    "getDialogAttachments",
                                    { dialogId: message.Fields.DialogId, fromHistory: false },
                                    draft => {
                                        draft.push(...newDefaultAttachments)
                                    }
                                )
                            )
                        }
                    }

                    if (isCurrentUserSender(message, store)) {
                        const isHoldEnabled =
                            message.Fields.Context?.find(ctx => ctx?.[0] === "hold:status")?.[1] === "enabled"
                        const isAutoHoldingMessage = message.Fields.Meta?.["is-autoholding-message"]

                        if (isHoldEnabled || isAutoHoldingMessage) {
                            const badgeIndex = dialogBadges?.findIndex(
                                dialogBadge => dialogBadge.Client.OmniUserId === currentMessagePagination?.OmniUserId
                            )

                            if (badgeIndex !== undefined && badgeIndex > -1) {
                                const badge = dialogBadges?.[badgeIndex]
                                if (badge) {
                                    const patchedBadge = isHoldEnabled ? { ...badge, IsHoldEnabled: true } : badge

                                    dispatch(updateDialogBadge(badgeIndex, patchedBadge, message))
                                }
                            }
                        }

                        if (message.Fields.MessageType !== MessageType.InternalOperatorMessage) {
                            dispatch(updateCurrentDialogBadgeWithOutgoing(message.Fields.DialogId))
                        }

                        const projectId = selectCurrentProjectId(store.getState())
                        if (
                            currentMessagePagination?.OmniUserId &&
                            projectId &&
                            selectedDialogId === message.Fields.DialogId
                        ) {
                            const getDialogSelector = dialogsApi.endpoints.getDialogMessages.select({
                                ...currentMessagePagination,
                                ProjectId: projectId
                            })
                            const getDialogQuery = getDialogSelector(store.getState())
                            const isNewMessage = !(
                                getDialogQuery.data &&
                                getDialogQuery.data.Messages.some(Message => Message.Fields.Id === message.Fields.Id)
                            )
                            const isMessageTextType = message.Fields.TextType === MessageTextType.Text

                            if (isNewMessage && isMessageTextType) {
                                const currentOperator = store.getState().users.currentUser
                                message.Fields.Sender.Id = currentOperator.data?.Login || message.Fields.Sender.Id

                                dispatch(
                                    dialogsApi.util.updateQueryData(
                                        "getDialogMessages",
                                        currentMessagePagination,
                                        oldState => {
                                            oldState.Messages.unshift(message)
                                        }
                                    )
                                )

                                dispatch(
                                    getAIAssistHint(
                                        message.Fields.OmniUserId ?? message.Fields.Sender.Id,
                                        dialogProjectId,
                                        false,
                                        selectedDialogId,
                                        currentOperator.data?.Locale
                                    )
                                )
                                dispatch(getAISuggestsByDialogId(selectedDialogId))
                            }
                        }

                        return
                    }

                    if (selectedDialogId === message.Fields.DialogId && currentMessagePagination) {
                        dispatch(
                            dialogsApi.util.updateQueryData("getDialogMessages", currentMessagePagination, oldState => {
                                oldState.Messages.unshift(message)
                            })
                        )

                        dispatch(
                            getAIAssistHint(
                                message.Fields.OmniUserId ?? message.Fields.Sender.Id,
                                dialogProjectId,
                                false,
                                selectedDialogId,
                                selectCurrentUserLanguage(store.getState())
                            )
                        )
                        dispatch(getAISuggestsByDialogId(selectedDialogId))
                    }

                    if (!isCached) {
                        dialogBadges = (await dispatch(refetchDialogBadges())).data
                    }

                    const badgeIndex = dialogBadges?.findIndex(
                        dialogBadge => dialogBadge.Client.OmniUserId === message.Fields.Sender.Id
                    )

                    if (badgeIndex !== undefined && badgeIndex > -1) {
                        const badge = dialogBadges?.[badgeIndex]

                        if (badge) {
                            const title =
                                getClientFullname(badge.Client) ||
                                i18next.t(`${tDialogsNotificationsNamespace}unknown-client`)
                            const unreadMessages = i18next.t(`${tDialogsNotificationsNamespace}unread-messages`, {
                                value: badge.UnreadMessages
                            })
                            const body = `${unreadMessages} ${message.Fields.Text}`
                            const dialogId = message.Fields.DialogId

                            if (!isStarterMessage(message)) {
                                dispatch(updateDialogBadge(badgeIndex, badge, message))
                            }
                            dispatch(dialogsApi.endpoints.putDisableDialogHold.initiate(dialogId))

                            if (!isStarterMessage(message)) {
                                notifyOperator(
                                    {
                                        title,
                                        body,
                                        dialogId,
                                        dispatch,
                                        projectId: badge.Project.Id,
                                        clientId: message.Fields.Sender.Id
                                    },
                                    { type: NotificationsTypes.NewMessage, message: String(badge.UnreadMessages + 1) }
                                )
                            }
                        }
                    }

                    dispatch(
                        actions.setIsUserTyping({
                            isTyping: false,
                            message: ""
                        })
                    )

                    dispatch(refetchActiveDialogsCount())
                } catch (e) {
                    logHubError(HUB_NAME, Events.HANDLE_MESSAGE, e)
                }
            },
            [Events.PINNED_MESSAGES]: async (data: unknown) => {
                try {
                    const currentMessagePagination = selectMessagesPaginationState(store.getState())

                    const incomingEntity = data as HubPinnedMessages

                    if (!currentMessagePagination) return

                    dispatch(
                        dialogsApi.util.upsertQueryData("getPinnedMessages", currentMessagePagination.OmniUserId, {
                            Messages: incomingEntity.Messages ?? []
                        })
                    )
                } catch (e) {
                    logHubError(HUB_NAME, Events.PINNED_MESSAGES, e)
                }
            },
            [Events.HOLD_DIALOG_UPDATED_MESSAGE]: async (data: unknown) => {
                try {
                    const processed = data as BadgeHoldStatusUpdate

                    if (!processed || !processed.HoldStatus) return

                    dispatch(
                        updateDialogBadges(oldState => {
                            const dialogById = oldState.findIndex(d => d.Id === processed.DialogId)

                            if (dialogById > -1 && processed.HoldStatus) {
                                oldState[dialogById].IsHoldEnabled = processed.HoldStatus === "enabled"
                            }
                        })
                    )
                } catch (e) {
                    logHubError(HUB_NAME, Events.HOLD_DIALOG_UPDATED_MESSAGE, e)
                }
            },
            [Events.LINKED_CLIENT_ADDED]: async (data: unknown) => {
                try {
                    const addedClientMessage = data as { Badge: DialogBadge }

                    const client = getLinkedClientFromSubject(addedClientMessage.Badge)
                    client.ProjectId = addedClientMessage.Badge.Project.Id

                    updateLinkedClients(dispatch, UserAccessActions.Added, client, isShowLinkedClients())
                } catch (e) {
                    logHubError(HUB_NAME, Events.LINKED_CLIENT_ADDED, e)
                }
            },
            [Events.LINKED_CLIENT_REMOVED]: async (data: unknown) => {
                try {
                    const removedClientMessage = data as { Badge: DialogBadge }

                    const client = getLinkedClientFromSubject(removedClientMessage.Badge)
                    client.ProjectId = removedClientMessage.Badge.Project.Id

                    updateLinkedClients(dispatch, UserAccessActions.Removed, client, isShowLinkedClients())
                } catch (e) {
                    logHubError(HUB_NAME, Events.LINKED_CLIENT_REMOVED, e)
                }
            },
            [Events.START_AUTO_HOLDING_TIMER]: async (data: unknown) => {
                try {
                    const message = data as AutoHoldingMessage
                    const { DialogId, AutoHolding } = message

                    if (!AutoHolding) {
                        return
                    }

                    dispatch(
                        actions.setAutoHoldingTimerForDialog({
                            DialogId: DialogId,
                            AutoHolding: AutoHolding
                        })
                    )
                } catch (e) {
                    logHubError(HUB_NAME, Events.START_AUTO_HOLDING_TIMER, e)
                }
            },
            [Events.STOP_AUTO_HOLDING_TIMER]: async (data: unknown) => {
                try {
                    const dialogId = data as string

                    dispatch(actions.removeAutoHoldingTimerFromDialog(dialogId))
                } catch (e) {
                    logHubError(HUB_NAME, Events.STOP_AUTO_HOLDING_TIMER, e)
                }
            }
        }
    }

    registerServerEvents() {
        handleEvents(this._events, this._hub.registerEvent.bind(this._hub))
    }

    unregisterServerEvents() {
        handleEvents(this._events, this._hub.unregisterEvent.bind(this._hub))
    }
}
